import { useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { ProgressBar } from '@/components/common/ProgressBar'
import { AlertTriangle, AlertCircle, FileText, Loader2 } from 'lucide-react'
import { bulkTimetableImportApi } from '@/services/api/bulkTimetableImport'
import { timetableImportV2Api } from '@/services/api/timetableImportV2'

interface BulkImportProgressProps {
  jobId: string
  batchId: string
  onCompleted: (batchId: string) => void
  onCancel: () => void
}

interface SectionInfo {
  section_index: number
  section_identifier: string | null
  section_pages: number[]
  session_id: string | null
  status: string
  error: string | null
  has_unmatched: boolean | null
  matched_count: number | null
  unmatched_count: number | null
  complete_entries?: number
  total_entries?: number
}

/** Maximum duration for fallback polling before giving up (5 minutes). */
const MAX_POLL_DURATION_MS = 5 * 60 * 1000

export function BulkImportProgress({
  jobId,
  batchId,
  onCompleted,
  onCancel,
}: BulkImportProgressProps) {
  const { t } = useTranslation()
  const [progress, setProgress] = useState(0)
  const [message, setMessage] = useState('')
  const [sections, setSections] = useState<SectionInfo[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [completed, setCompleted] = useState(false)
  const [stale, setStale] = useState(false)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef(0)
  const completedOnceRef = useRef(false)

  const onCompletedRef = useRef(onCompleted)
  onCompletedRef.current = onCompleted

  const finish = useCallback((reason: 'completed' | 'failed' | 'cancelled') => {
    setCompleted(true)
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    if (reason === 'failed' || reason === 'cancelled') return
    if (!completedOnceRef.current) {
      completedOnceRef.current = true
      onCompletedRef.current(batchId)
    }
  }, [batchId])

  const fetchStatus = useCallback(async () => {
    try {
      const data = await bulkTimetableImportApi.getJobStatus(jobId)
      setProgress(data.progress)
      setMessage(data.message)

      if (data.result?.total_pages) {
        setTotalPages(data.result.total_pages as number)
      }

      if (data.batch_id) {
        try {
          const batchData = await timetableImportV2Api.getBatchStatus(data.batch_id)
          setSections((batchData.sections || []) as SectionInfo[])
        } catch {
          // batch may not exist yet
        }
      }

      const batchReady =
        data.batch_status === 'pending_bulk_shop' ||
        data.batch_status === 'resolved' ||
        data.batch_status === 'partially_failed' ||
        (data.batch_status === 'resolving' && data.paused_at_section != null)
      const jobDone = data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled'
      if (jobDone || batchReady) {
        if (data.status === 'failed' || data.status === 'cancelled') {
          setMessage(data.message || `Job ${data.status}`)
          finish(data.status)
        } else {
          finish('completed')
        }
      }
    } catch {
      if (Date.now() - startTimeRef.current > MAX_POLL_DURATION_MS) {
        setStale(true)
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      }
    }
  }, [jobId, finish])

  useEffect(() => {
    let eventSource: EventSource | null = null
    startTimeRef.current = Date.now()

    let cancelled = false
    const startPolling = () => {
      fetchStatus()
      pollingRef.current = setInterval(fetchStatus, 3000)
    }
    void bulkTimetableImportApi.createJobEventSource(jobId).then((source) => {
      if (cancelled) {
        source.close()
        return
      }
      eventSource = source
      source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          setProgress(data.progress)
          setMessage(data.message)
          if (data.result?.total_pages) {
            setTotalPages(data.result.total_pages as number)
          }

          const batchReady =
            data.batch_status === 'pending_bulk_shop' ||
            data.batch_status === 'resolved' ||
            data.batch_status === 'partially_failed' ||
            (data.batch_status === 'resolving' && data.paused_at_section != null)
          if (data.status === 'completed' || batchReady) {
            finish('completed')
            eventSource?.close()
          } else if (data.status === 'failed' || data.status === 'cancelled') {
            setMessage(data.message || `Job ${data.status}`)
            finish(data.status)
            eventSource?.close()
          }
        } catch {
          // ignore parse errors
        }
      }
      source.onerror = () => {
        source.close()
        startPolling()
      }
    }).catch(() => {
      if (!cancelled) startPolling()
    })

    return () => {
      cancelled = true
      if (eventSource) eventSource.close()
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [jobId, batchId, fetchStatus, finish])

  const handleCancel = async () => {
    try {
      await timetableImportV2Api.cancelJob(jobId)
    } catch {
      // ignore
    }
    onCancel()
  }

  const statusIcons: Record<string, React.ReactNode> = {
    pending_resolution: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
    incomplete: <AlertTriangle className="h-4 w-4 text-orange-500" />,
    resolved: <FileText className="h-4 w-4 text-green-500" />,
    imported: <FileText className="h-4 w-4 text-blue-500" />,
    failed: <AlertCircle className="h-4 w-4 text-red-500" />,
    cancelled: <AlertCircle className="h-4 w-4 text-gray-400" />,
    skipped: <FileText className="h-4 w-4 text-gray-400" />,
  }

  const currentPage = message.match(/(\d+)\/(\d+)/)?.[1] || '—'

  // Stale/timeout guard for fallback polling
  if (stale) {
    return (
      <Modal isOpen={true} onClose={onCancel} title="Zeitüberschreitung" size="lg">
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0" />
            <div>
              <div className="font-medium text-amber-800">Verarbeitung dauert zu lange</div>
              <div className="text-sm text-amber-600 mt-1">
                Der Import konnte nicht innerhalb der erwarteten Zeit abgeschlossen werden.
                Du kannst das Fenster schliessen und es später erneut versuchen.
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="primary" size="sm" onClick={onCancel}>
              Schliessen
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={true}
      onClose={handleCancel}
      title={t('bulkImport_progress_title') as string}
      size="lg"
    >
      <div className="space-y-4">
        <ProgressBar
          current={Math.max(progress, 5)}
          total={100}
          label={t('bulkImport_progress_title') as string}
          className="h-3"
        />

        <div className="text-center">
          <div className="text-2xl font-bold text-gray-800">
            {currentPage}/{totalPages || '—'}
          </div>
          <div className="text-sm text-gray-500 mt-1">{message}</div>
        </div>

        {sections.length > 0 && (
          <div className="max-h-48 overflow-y-auto space-y-0.5 border rounded-lg">
            {sections.map((section, idx) => (
              <div
                // Use combination of section_index, session_id, and array index to ensure uniqueness
                key={`${section.section_index}-${section.session_id || idx}`}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 border-b last:border-b-0"
              >
                {statusIcons[section.status] || <FileText className="h-3.5 w-3.5 text-gray-400" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">
                    {section.section_identifier || `Seite ${section.section_pages?.join(', ')}`}
                  </div>
                  {/* Error message for failed sections */}
                  {section.status === 'failed' && section.error && (
                    <div className="text-xs text-red-500 truncate">{section.error}</div>
                  )}
                  {/* Progress for resolving sections */}
                  {section.status === 'pending_resolution' && (
                    <div className="text-xs text-yellow-600 flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {(t('bulkImport_resolutionNeeded') as string) || 'Auflösung nötig'}
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {section.status === 'resolved' ? 'OK' : section.status === 'pending_resolution' ? 'Offen' : section.status === 'failed' ? 'Fehler' : ''}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            {completed ? 'Schliessen' : t('bulkImport_progress_cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
